import type { RefrigerationUnitRow } from '../../db';
import { RefrigerationUnit } from '../../schemas';
import { newId } from '../../../utils/ids';
import { normalizeNameKey, trim, toNonNegativeNumber } from '../repoUtils';
import {
  isFieldValueCompatible,
  isFieldValueEmpty,
  normalizeFieldValue,
} from '../../fieldPatterns';
import { normalizeRefrigerationUnitTechnical } from './technical';
import {
  DuplicateRefrigerationUnitNameError,
  RefrigerationUnitFieldError,
  type RefrigerationUnitInput,
} from './types';
import { type FieldValue, coerceLegacyFieldValue } from '../../schemas/fieldPatterns.schemas';
import { normalizeIsoDate, normalizeIsoMonth, toIsoMonth } from '@/utils/date';

export function normalizeRefrigerationUnitInput(
  input: RefrigerationUnitInput | RefrigerationUnitRow,
  now: string,
  existing?: RefrigerationUnitRow,
  siblings: RefrigerationUnitRow[] = []
): RefrigerationUnitRow {
  const hasOwnProperty = <T extends object>(target: T, key: string): boolean =>
    Object.prototype.hasOwnProperty.call(target, key);

  const baseId = input.id ?? existing?.id ?? newId();
  const refrigerationUnitId = trim(input.refrigerationUnitId ?? existing?.refrigerationUnitId);
  if (!refrigerationUnitId) {
    throw new RefrigerationUnitFieldError('name', 'Identificativo impianto obbligatorio');
  }
  const name = trim(input.name ?? existing?.name);
  if (!name) {
    throw new RefrigerationUnitFieldError('name', 'Nome impianto obbligatorio');
  }
  const location = normalizeFieldValue(
    coerceLegacyFieldValue(
      (input.location ?? existing?.location) as FieldValue | null | undefined,
      'open_enum'
    )
  );
  if (!isFieldValueCompatible('open_enum', location)) {
    throw new RefrigerationUnitFieldError('location', 'Ubicazione impianto non valida');
  }
  if (isFieldValueEmpty(location)) {
    throw new RefrigerationUnitFieldError('location', 'Ubicazione impianto obbligatoria');
  }

  const duplicate = siblings.some(
    (refrigerationUnit) =>
      refrigerationUnit.id !== existing?.id &&
      refrigerationUnit.refrigerationUnitId.toLowerCase() === refrigerationUnitId.toLowerCase()
  );
  if (duplicate) {
    throw new RefrigerationUnitFieldError(
      'name',
      'Esiste già un impianto con questo identificativo'
    );
  }

  const duplicateName = siblings.some((refrigerationUnit) => {
    if (refrigerationUnit.id === existing?.id) return false;
    return normalizeNameKey(refrigerationUnit.name) === normalizeNameKey(name);
  });
  if (duplicateName) {
    throw new DuplicateRefrigerationUnitNameError(name);
  }

  const technical = normalizeRefrigerationUnitTechnical(input.technical, existing?.technical);

  const toxicGasType = normalizeFieldValue(
    coerceLegacyFieldValue(
      (input.toxicGasType ?? existing?.toxicGasType) as FieldValue | null | undefined,
      'closed_enum'
    )
  );
  if (!isFieldValueCompatible('closed_enum', toxicGasType)) {
    throw new RefrigerationUnitFieldError('toxicGasType', 'Gas tossico non valido');
  }
  if (isFieldValueEmpty(toxicGasType)) {
    throw new RefrigerationUnitFieldError('toxicGasType', 'Gas tossico obbligatorio');
  }

  const impiantoType = normalizeFieldValue(
    coerceLegacyFieldValue(
      (input.impiantoType ?? existing?.impiantoType) as FieldValue | null | undefined,
      'open_enum'
    )
  );
  if (!isFieldValueCompatible('open_enum', impiantoType)) {
    throw new RefrigerationUnitFieldError('impiantoType', 'Tipologia impianto non valida');
  }
  if (isFieldValueEmpty(impiantoType)) {
    throw new RefrigerationUnitFieldError('impiantoType', 'Tipologia impianto obbligatoria');
  }

  const hasPlannedDateInput = hasOwnProperty(input, 'plannedDate');
  const hasPlannedPeriodInput = hasOwnProperty(input, 'plannedPeriod');
  const hasPlannedSourceInput = hasOwnProperty(input, 'plannedSource');

  const plannedDateInput = hasPlannedDateInput
    ? (input as RefrigerationUnitInput).plannedDate
    : existing?.plannedDate;
  const plannedPeriodInput = hasPlannedPeriodInput
    ? (input as RefrigerationUnitInput).plannedPeriod
    : existing?.plannedPeriod;
  const plannedSourceInput = hasPlannedSourceInput
    ? (input as RefrigerationUnitInput).plannedSource
    : existing?.plannedSource;

  const normalizedPlannedDate = normalizeIsoDate(
    typeof plannedDateInput === 'string' ? plannedDateInput : undefined
  );
  const inferredPeriod = normalizedPlannedDate ? toIsoMonth(normalizedPlannedDate) : undefined;
  const normalizedPlannedPeriod = hasPlannedPeriodInput
    ? normalizeIsoMonth(typeof plannedPeriodInput === 'string' ? plannedPeriodInput : undefined)
    : normalizedPlannedDate
      ? inferredPeriod
      : normalizeIsoMonth(typeof plannedPeriodInput === 'string' ? plannedPeriodInput : undefined);

  const plannedSource = trim(
    typeof plannedSourceInput === 'string' ? plannedSourceInput : undefined
  );

  const plannedFieldsChanged =
    (hasPlannedDateInput && normalizedPlannedDate !== existing?.plannedDate) ||
    (hasPlannedPeriodInput && normalizedPlannedPeriod !== existing?.plannedPeriod) ||
    (hasPlannedSourceInput && plannedSource !== existing?.plannedSource);

  const plannedUpdatedAt = plannedFieldsChanged ? now : existing?.plannedUpdatedAt;

  const refrigerationUnit = RefrigerationUnit.parse({
    id: baseId,
    refrigerationUnitId,
    name,
    location,
    ammoniaKg: toNonNegativeNumber(input.ammoniaKg ?? existing?.ammoniaKg),
    toxicGasType,
    impiantoType,
    plannedPeriod: normalizedPlannedPeriod,
    plannedDate: normalizedPlannedDate,
    plannedSource,
    plannedUpdatedAt,
    technical,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    deletedAt: existing?.deletedAt,
  });

  return refrigerationUnit;
}
